// See App.tsx's own comment on this same import -- required for the
// classic JSX transform this app's tsconfig uses.
import * as React from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { updateCitizenContext, useCitizenContext } from '@accenturecodefoundry/mfe-pot-citizen-context';
import { useEmploymentSummary } from '@accenturecodefoundry/mfe-pot-epayroll';
import type { ContentClient } from '@accenturecodefoundry/mfe-pot-shared-content-client';
import type { Locale } from '@accenturecodefoundry/mfe-pot-shared-i18n';
import type { EiApplicationInput, EmploymentInsuranceApiClient } from 'employment-insurance-data-access';
import { calculateWeeklyBenefitAmount } from 'employment-insurance-data-access';
import { APPLICATION_FORM_CONTENT_KEYS, type ApplicationFormContentKey } from './content-client';
import { EiPreFillConfirmStep, type InsurableTotals, type ProfileStatus } from './EiPreFillConfirmStep';
import { usePageContents } from './use-page-contents';
import './EiApplicationForm.css';

export interface EiApplicationFormProps {
  apiClient: EmploymentInsuranceApiClient;
  contentClient: ContentClient;
  locale: Locale;
  submitting: boolean;
  onSubmit: (application: EiApplicationInput) => void;
}

/**
 * Flat draft shape for the wizard's in-progress answers -- every field is
 * a plain string (or `number | null` for the one currency field, or a
 * boolean for the native checkboxes) because that's what
 * `scds-text-input`/`scds-picker`/`scds-currency-input`/`<input
 * type="checkbox">` emit; `toApplicationInput` below is the one place this
 * gets converted into the real typed `EiApplicationInput` shape on final
 * submit. Every key here doubles as the `data-field` value set on that
 * field's JSX element (see the component's own comment on why `data-field`
 * + delegated `addEventListener`, not an `onScdsChange` prop).
 */
export interface FormDraft {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  province: string;
  postalCode: string;
  phone: string;
  preferredLanguage: string;
  employerName: string;
  lastDayWorked: string;
  reasonCode: string;
  payRate: number | null;
  payPeriod: string;
  jobTitle: string;
  hadOtherEmployers: string;
  otherEmployerName: string;
  // Spec 013's "other situations" checklist: plain booleans, one per box.
  workersCompensation: boolean;
  pension: boolean;
  selfEmployedOrBusiness: boolean;
  inTrainingProgram: boolean;
  unionMember: boolean;
  quebecParentalInsurance: boolean;
  relatedToEmployer: boolean;
  unableToWorkMedical: boolean;
  incarcerated: boolean;
  pregnantOrRecentBirth: boolean;
  availableImmediately: string;
  availableFromDate: string;
  educationLevel: string;
  // 'yes' = direct deposit, 'no' = cheque by mail (step 3's payment method).
  directDepositEnrolling: string;
  institutionNumber: string;
  transitNumber: string;
  accountNumber: string;
  // Spec 014 additions: step 3's tax preferences ('' until picked), step 4's
  // three consents and third-party assistance ('yes' | 'no' | '').
  taxSlipDelivery: string;
  taxDeduction: string;
  statementsConfirmed: boolean;
  consentToShare: boolean;
  rightsAccepted: boolean;
  thirdPartyAssistance: string;
  helperName: string;
}

const EMPTY_DRAFT: FormDraft = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  province: '',
  postalCode: '',
  phone: '',
  preferredLanguage: '',
  employerName: '',
  lastDayWorked: '',
  reasonCode: '',
  payRate: null,
  payPeriod: '',
  jobTitle: '',
  hadOtherEmployers: '',
  otherEmployerName: '',
  workersCompensation: false,
  pension: false,
  selfEmployedOrBusiness: false,
  inTrainingProgram: false,
  unionMember: false,
  quebecParentalInsurance: false,
  relatedToEmployer: false,
  unableToWorkMedical: false,
  incarcerated: false,
  pregnantOrRecentBirth: false,
  availableImmediately: '',
  availableFromDate: '',
  educationLevel: '',
  directDepositEnrolling: '',
  institutionNumber: '',
  transitNumber: '',
  accountNumber: '',
  taxSlipDelivery: '',
  taxDeduction: '',
  statementsConfirmed: false,
  consentToShare: false,
  rightsAccepted: false,
  thirdPartyAssistance: '',
  helperName: '',
};

export type FieldName = keyof FormDraft;

const TOTAL_STEPS = 4;

type OtherSituationField =
  | 'unionMember'
  | 'selfEmployedOrBusiness'
  | 'inTrainingProgram'
  | 'pension'
  | 'workersCompensation'
  | 'quebecParentalInsurance'
  | 'relatedToEmployer'
  | 'unableToWorkMedical'
  | 'incarcerated'
  | 'pregnantOrRecentBirth';

/**
 * Spec 013's ten "other situations" checkboxes, in the prototype's on-screen
 * order. The four existing eligibility fields reuse their old label keys.
 */
const OTHER_SITUATION_OPTIONS: ReadonlyArray<{ field: OtherSituationField; labelKey: ApplicationFormContentKey }> = [
  { field: 'unionMember', labelKey: 'employment-insurance.application.unionMemberLabel' },
  { field: 'selfEmployedOrBusiness', labelKey: 'employment-insurance.application.selfEmployedLabel' },
  { field: 'inTrainingProgram', labelKey: 'employment-insurance.application.trainingProgramLabel' },
  { field: 'pension', labelKey: 'employment-insurance.application.pensionLabel' },
  { field: 'workersCompensation', labelKey: 'employment-insurance.application.workersCompensationLabel' },
  { field: 'quebecParentalInsurance', labelKey: 'employment-insurance.application.quebecParentalInsuranceLabel' },
  { field: 'relatedToEmployer', labelKey: 'employment-insurance.application.relatedToEmployerLabel' },
  { field: 'unableToWorkMedical', labelKey: 'employment-insurance.application.unableToWorkMedicalLabel' },
  { field: 'incarcerated', labelKey: 'employment-insurance.application.incarceratedLabel' },
  { field: 'pregnantOrRecentBirth', labelKey: 'employment-insurance.application.pregnantOrRecentBirthLabel' },
];

type ConsentField = 'statementsConfirmed' | 'consentToShare' | 'rightsAccepted';

/** Step 4's three required declaration consents (spec 014), in screen order. */
const CONSENT_OPTIONS: ReadonlyArray<{ field: ConsentField; labelKey: ApplicationFormContentKey }> = [
  { field: 'statementsConfirmed', labelKey: 'employment-insurance.application.statementsConfirmedLabel' },
  { field: 'consentToShare', labelKey: 'employment-insurance.application.consentToShareLabel' },
  { field: 'rightsAccepted', labelKey: 'employment-insurance.application.rightsAcceptedLabel' },
];

/** The declaration statements the first consent confirms (from the prototype). */
const DECLARATION_STATEMENT_KEYS: ReadonlyArray<ApplicationFormContentKey> = [
  'employment-insurance.application.declarationStatement1',
  'employment-insurance.application.declarationStatement2',
  'employment-insurance.application.declarationStatement3',
  'employment-insurance.application.declarationStatement4',
];

/**
 * epayroll's separation reason -> this form's reasonCode picker options
 * (docs/specs/012-ei-application-prefill.md). 'illness' has no direct match
 * among the four options this form offers, so it maps to 'other' -- a
 * deliberate PoC simplification the spec records. The citizen can always
 * correct the pre-selected value on step 1's "Your last job" card.
 */
const EPAYROLL_REASON_CODES: Record<string, string> = {
  layoff: 'shortage_of_work',
  quit: 'quit',
  illness: 'other',
  other: 'other',
};

function isBlank(value: string): boolean {
  return value.trim() === '';
}

/** Missing required fields for the given step, given the current draft. */
function validateStep(
  step: number,
  draft: FormDraft,
  profileStatus: ProfileStatus,
  separationPreFilled: boolean,
): Set<FieldName> {
  const missing = new Set<FieldName>();
  const require = (condition: boolean, field: FieldName) => {
    if (condition) {
      missing.add(field);
    }
  };

  switch (step) {
    case 1:
      // Confirm what we already know (spec 014). Every field with no data
      // source is an input here and required. Sourced fields render
      // read-only and aren't re-validated (fixed at msca-shell's /profile,
      // or the employer's ROE) -- except when their source is unavailable,
      // in which case they become inputs and are validated too: name, phone
      // and language when the profile is degraded (the removed "Confirm
      // your details" step's rule), employer and last day worked when
      // epayroll didn't pre-fill them.
      require(isBlank(draft.dateOfBirth), 'dateOfBirth');
      require(isBlank(draft.addressLine1), 'addressLine1');
      require(isBlank(draft.city), 'city');
      require(isBlank(draft.province), 'province');
      require(isBlank(draft.postalCode), 'postalCode');
      require(isBlank(draft.educationLevel), 'educationLevel');
      if (profileStatus === 'degraded') {
        require(isBlank(draft.firstName), 'firstName');
        require(isBlank(draft.lastName), 'lastName');
        require(isBlank(draft.phone), 'phone');
        require(isBlank(draft.preferredLanguage), 'preferredLanguage');
      }
      if (!separationPreFilled) {
        require(isBlank(draft.employerName), 'employerName');
        require(isBlank(draft.lastDayWorked), 'lastDayWorked');
      }
      require(isBlank(draft.jobTitle), 'jobTitle');
      require(isBlank(draft.reasonCode), 'reasonCode');
      require(draft.payRate === null, 'payRate');
      require(isBlank(draft.payPeriod), 'payPeriod');
      require(isBlank(draft.hadOtherEmployers), 'hadOtherEmployers');
      require(draft.hadOtherEmployers === 'yes' && isBlank(draft.otherEmployerName), 'otherEmployerName');
      break;
    case 2:
      // Availability is required; spec 013's checklist is all optional.
      require(isBlank(draft.availableImmediately), 'availableImmediately');
      require(draft.availableImmediately === 'no' && isBlank(draft.availableFromDate), 'availableFromDate');
      break;
    case 3:
      require(isBlank(draft.directDepositEnrolling), 'directDepositEnrolling');
      require(draft.directDepositEnrolling === 'yes' && isBlank(draft.institutionNumber), 'institutionNumber');
      require(draft.directDepositEnrolling === 'yes' && isBlank(draft.transitNumber), 'transitNumber');
      require(draft.directDepositEnrolling === 'yes' && isBlank(draft.accountNumber), 'accountNumber');
      require(isBlank(draft.taxSlipDelivery), 'taxSlipDelivery');
      require(isBlank(draft.taxDeduction), 'taxDeduction');
      break;
    case 4:
      require(!draft.statementsConfirmed, 'statementsConfirmed');
      require(!draft.consentToShare, 'consentToShare');
      require(!draft.rightsAccepted, 'rightsAccepted');
      require(isBlank(draft.thirdPartyAssistance), 'thirdPartyAssistance');
      break;
  }
  return missing;
}

function toApplicationInput(draft: FormDraft): EiApplicationInput {
  const hadOtherEmployers = draft.hadOtherEmployers === 'yes';
  const availableImmediately = draft.availableImmediately === 'yes';
  const enrolling = draft.directDepositEnrolling === 'yes';
  const thirdPartyAssistance = draft.thirdPartyAssistance === 'yes';
  return {
    personal: {
      firstName: draft.firstName,
      lastName: draft.lastName,
      dateOfBirth: draft.dateOfBirth,
      addressLine1: draft.addressLine1,
      city: draft.city,
      province: draft.province,
      postalCode: draft.postalCode,
      phone: draft.phone,
      preferredLanguage: draft.preferredLanguage === 'fr' ? 'fr' : 'en',
    },
    separation: {
      employerName: draft.employerName,
      lastDayWorked: draft.lastDayWorked,
      reasonCode: draft.reasonCode as EiApplicationInput['separation']['reasonCode'],
      payRate: draft.payRate ?? 0,
      payPeriod: draft.payPeriod as EiApplicationInput['separation']['payPeriod'],
      jobTitle: draft.jobTitle,
    },
    otherEmployment: {
      hadOtherEmployers,
      otherEmployerName: hadOtherEmployers ? draft.otherEmployerName : undefined,
    },
    eligibility: {
      workersCompensation: draft.workersCompensation,
      pension: draft.pension,
      selfEmployedOrBusiness: draft.selfEmployedOrBusiness,
      inTrainingProgram: draft.inTrainingProgram,
      unionMember: draft.unionMember,
      quebecParentalInsurance: draft.quebecParentalInsurance,
      relatedToEmployer: draft.relatedToEmployer,
      unableToWorkMedical: draft.unableToWorkMedical,
      incarcerated: draft.incarcerated,
      pregnantOrRecentBirth: draft.pregnantOrRecentBirth,
    },
    availability: {
      availableImmediately,
      availableFromDate: availableImmediately ? undefined : draft.availableFromDate,
      educationLevel: draft.educationLevel as EiApplicationInput['availability']['educationLevel'],
    },
    directDeposit: {
      enrolling,
      institutionNumber: enrolling ? draft.institutionNumber : undefined,
      transitNumber: enrolling ? draft.transitNumber : undefined,
      accountNumber: enrolling ? draft.accountNumber : undefined,
    },
    taxSlipDelivery: draft.taxSlipDelivery as EiApplicationInput['taxSlipDelivery'],
    taxDeduction: draft.taxDeduction as EiApplicationInput['taxDeduction'],
    statementsConfirmed: draft.statementsConfirmed,
    consentToShare: draft.consentToShare,
    rightsAccepted: draft.rightsAccepted,
    thirdPartyAssistance,
    helperName: thirdPartyAssistance && !isBlank(draft.helperName) ? draft.helperName : undefined,
  };
}

/**
 * The EI application wizard: the prototype's 4-step flow
 * (docs/specs/014-ei-application-flow-consolidation.md), condensed from the
 * real Service Canada online EI application (confirmed screen by screen
 * against a citizen's-guide walkthrough of the live canada.ca flow plus the
 * official "Regular benefits" checklist).
 *
 *  1. Confirm what we already know  <- Identity/Personal Information, Last
 *                                       Employer, Reasons for Separation,
 *                                       Rate of Pay, Job Title, ROE, Other
 *                                       Employers, education level. Each
 *                                       field once: read-only where
 *                                       citizen-context/epayroll supply it,
 *                                       editable where nothing does
 *                                       (EiPreFillConfirmStep).
 *  2. A few things only you can     <- availability, plus spec 013's
 *     answer                           optional "other situations"
 *                                       checklist (Workers' Compensation,
 *                                       Pensions, Business Relationship,
 *                                       Course or Training, ...)
 *  3. How you'll be paid            <- Banking/Direct Deposit Information,
 *                                       T4E delivery, personal tax deduction
 *  4. Review and submit             <- weekly benefit estimate, the
 *                                       declaration's three consents (incl.
 *                                       Rights and Responsibilities),
 *                                       third-party assistance
 *
 * Deliberately dropped (not silently missed -- see the plan/TODO):
 * identity verification & temporary password (redundant with this PoT's
 * real PKCE sign-in), the Quebec Parental Insurance Plan sub-flow (spec 013
 * captures only a yes/no flag), farming income, the "variable best weeks"
 * manual per-week earnings table, the training-program sub-flow's detailed
 * questions, and (spec 014) SIN, email, gender, separate spoken/written
 * language and a secondary phone.
 *
 * No React Router -- remotes have no internal routing in this family (see
 * the platform repo's CLAUDE.md, "Security: defense in depth"); step state
 * is plain `useState`. Every field is a controlled prop, same as
 * `scds-badge`'s `tone` -- this component owns no persisted state itself,
 * it just collects answers and hands the assembled `EiApplicationInput` to
 * `onSubmit` once the declaration is accepted.
 *
 * **Field changes are read via one delegated `addEventListener` pair on
 * this component's own root `<div>`, not `onScdsChange`/`onScdsInput` JSX
 * props.** Confirmed empirically (see `scds-elements.d.ts`'s own comment):
 * React 19 does not wire an `onScdsChange`-shaped prop to a real
 * `addEventListener` call for a custom element the way it does for a
 * recognized native event like `onClick`/`onChange` -- it silently sets an
 * inert same-named property instead, so the handler would compile but
 * never fire. `scdsChange`/`scdsInput` are real `composed: true` custom
 * events, so they bubble out through every element's shadow boundary up
 * to this root div, where one listener reads `event.target`'s `data-field`
 * attribute (set on every field below) to know which `FormDraft` key to
 * update -- every `data-field` value is deliberately spelled to match a
 * `FormDraft` key exactly, so the generic handler needs no per-field
 * mapping table.
 */
export function EiApplicationForm({ apiClient, contentClient, locale, submitting, onSubmit }: EiApplicationFormProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState<FormDraft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<Set<FieldName>>(new Set());
  // Write-back-to-UBE state for step 1's address.
  const [patching, setPatching] = useState(false);
  const [patchError, setPatchError] = useState<string | null>(null);
  const { content, loadedLocale } = usePageContents(contentClient, APPLICATION_FORM_CONTENT_KEYS, locale);
  // Called here, not in the step render functions: those only run on their
  // own step, and a hook behind a step switch would change the hook count
  // between renders.
  const checklistHintId = useId();
  const declarationErrorId = useId();
  const { data: profile, status: profileStatus } = useCitizenContext();
  const { data: employment, status: employmentStatus } = useEmploymentSummary();
  // The one gate for every epayroll-separation path (draft pre-fill, and
  // step 1's read-only vs editable employer/last day): currentStatus ===
  // 'separated', per spec 012. A re-employed citizen can still carry an
  // older mostRecentSeparation; keying any path off that field alone would
  // pre-fill and lock a stale separation.
  const separation =
    employmentStatus === 'ready' && employment?.currentStatus === 'separated'
      ? (employment.mostRecentSeparation ?? null)
      : null;
  // Insurable totals for step 1's two read-only rows only (spec 012):
  // reference data from the ROE, deliberately NOT part of FormDraft and
  // never submitted -- so it lives in its own state, not the draft.
  const [insurableTotals, setInsurableTotals] = useState<InsurableTotals>({
    insurableHours: null,
    insurableEarnings: null,
  });

  // Pre-fill the citizen-context-owned fields the first time the FES has a
  // ready profile. Every guard is `prev.x || ...` rather than an
  // unconditional overwrite so a focus refetch that lands mid-address-edit
  // doesn't clobber the user's in-progress typing. FES `name` is a single
  // field per its frozen v1 contract; the EI application still splits it
  // into first/last, so this is where the two shapes reconcile (naive
  // whitespace split -- deliberate PoT simplification, mirrors the fixture
  // shape "Jordan Tremblay"). preferredLanguage narrows to the EI-form
  // union ('en' | 'fr') implicitly since the FES only ever stores those
  // two.
  useEffect(() => {
    if (profileStatus !== 'ready' || !profile) {
      return;
    }
    const nameParts = (profile.name ?? '').trim().split(/\s+/);
    const firstName = nameParts.shift() ?? '';
    const lastName = nameParts.join(' ');
    setDraft((prev) => ({
      ...prev,
      firstName: prev.firstName || firstName,
      lastName: prev.lastName || lastName,
      addressLine1: prev.addressLine1 || (profile.address?.line1 ?? ''),
      addressLine2: prev.addressLine2 || (profile.address?.line2 ?? ''),
      city: prev.city || (profile.address?.city ?? ''),
      province: prev.province || (profile.address?.province ?? ''),
      postalCode: prev.postalCode || (profile.address?.postalCode ?? ''),
      phone: prev.phone || (profile.phone ?? ''),
      preferredLanguage: prev.preferredLanguage || (profile.preferredLanguage ?? ''),
    }));
  }, [profileStatus, profile]);

  // Pre-fill the epayroll-owned separation fields (spec 012). Deliberately
  // the same shape as the citizen-context effect above -- same early-return
  // guard, same `prev.x || ...` per field so a refetch never clobbers
  // in-progress typing -- and it co-exists with it rather than replacing
  // it: the two own disjoint parts of the draft.
  //
  // reasonCode is pre-filled into the draft but stays an editable picker on
  // step 1: the employer's stated reason is the one piece here a citizen
  // may legitimately need to correct.
  useEffect(() => {
    if (!separation) {
      return;
    }
    setDraft((prev) => ({
      ...prev,
      employerName: prev.employerName || separation.employerName,
      lastDayWorked: prev.lastDayWorked || separation.effectiveDate,
      reasonCode: prev.reasonCode || (EPAYROLL_REASON_CODES[separation.reasonCode] ?? 'other'),
    }));
  }, [separation]);

  // Insurable totals for step 1's two informational rows. The client
  // flattens every failure to a null pair (see its own comment), so there
  // is no error branch here -- null simply means "omit those rows".
  useEffect(() => {
    let cancelled = false;
    apiClient
      .getApplicationPreFill()
      .then((totals) => {
        if (!cancelled) {
          setInsurableTotals(totals);
        }
      })
      .catch(() => {
        // Unreachable in practice -- the client never rejects -- but a
        // rejected promise here must not become an unhandled rejection.
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    function handleFieldEvent(event: Event): void {
      const field = (event.target as HTMLElement).getAttribute('data-field');
      if (!field) {
        return;
      }
      const detail = (event as CustomEvent).detail;
      setDraft((prev) => ({ ...prev, [field]: detail }) as FormDraft);
    }
    // Only plain `data-field` inputs are handled here (every scds-text-input,
    // scds-picker and scds-currency-input on every step). The address input
    // group's `scdsAddressChange` is owned by AddressField itself, so its
    // mount-time value sync fires when step 1 renders -- a form-root effect
    // wouldn't re-run on step change.
    root.addEventListener('scdsChange', handleFieldEvent);
    root.addEventListener('scdsInput', handleFieldEvent);
    return () => {
      root.removeEventListener('scdsChange', handleFieldEvent);
      root.removeEventListener('scdsInput', handleFieldEvent);
    };
  }, []);

  function label(key: ApplicationFormContentKey): string {
    return content[key]?.title ?? key;
  }

  function fieldError(field: FieldName): string | undefined {
    return errors.has(field) ? label('employment-insurance.application.requiredError') : undefined;
  }

  function goBack(): void {
    setErrors(new Set());
    setPatchError(null);
    setStep((current) => Math.max(1, current - 1));
  }

  /**
   * Step 1's address is pre-filled from citizen-context and editable. When
   * the citizen continues with a changed address it is written back to the
   * citizen-context UBE first (spec 014; the behaviour of the removed
   * "Confirm your details" step). If that PATCH fails the wizard stays on
   * step 1 with an inline error rather than carrying a stale profile
   * forward. Only `{ address }` is written back; name/phone/language typed
   * in degraded mode flow into the claim via toApplicationInput, not the
   * profile. Returns whether the wizard may advance.
   */
  async function saveAddressIfChanged(): Promise<boolean> {
    const saved = profile?.address;
    const changed =
      draft.addressLine1 !== (saved?.line1 ?? '') ||
      draft.addressLine2 !== (saved?.line2 ?? '') ||
      draft.city !== (saved?.city ?? '') ||
      draft.province !== (saved?.province ?? '') ||
      draft.postalCode !== (saved?.postalCode ?? '');
    if (!changed) {
      return true;
    }
    setPatchError(null);
    setPatching(true);
    try {
      await updateCitizenContext({
        address: {
          line1: draft.addressLine1,
          line2: draft.addressLine2,
          city: draft.city,
          province: draft.province,
          postalCode: draft.postalCode,
        },
      });
      return true;
    } catch (err) {
      console.error('Failed to save address to citizen-context from the EI application', err);
      setPatchError(label('employment-insurance.application.profileSaveError'));
      return false;
    } finally {
      setPatching(false);
    }
  }

  async function goNext(): Promise<void> {
    const missing = validateStep(step, draft, profileStatus, separation !== null);
    if (missing.size > 0) {
      setErrors(missing);
      return;
    }
    setErrors(new Set());
    if (step === 1 && !(await saveAddressIfChanged())) {
      return;
    }
    if (step === TOTAL_STEPS) {
      onSubmit(toApplicationInput(draft));
      return;
    }
    setStep((current) => current + 1);
  }

  // Disables the footer buttons while a submit or profile write-back is in
  // flight. Each scds-button's onClick also checks it, not just the
  // `disabled` prop: that disables the component's inner shadow <button>,
  // but a click landing on the host element itself would still bubble to
  // React's handler.
  const busy = submitting || patching;

  const stepTitles = [1, 2, 3, 4].map((n) =>
    label(`employment-insurance.application.step${n}Title` as ApplicationFormContentKey),
  );

  function renderYesNoPicker(field: FieldName, labelKey: ApplicationFormContentKey, value: string) {
    // `key` is load-bearing, not cosmetic: scds-picker reads its
    // scds-picker-option children's text once, in componentWillLoad, so it
    // must remount when that text changes -- another field's options (bug
    // #186), or CMS labels arriving after first render or after a language
    // switch (keyed on the loaded content's locale). See usePageContents's
    // loadedLocale; every picker in the wizard is keyed the same way.
    return (
      <scds-picker
        key={`${field}-${loadedLocale ?? 'loading'}`}
        data-field={field}
        label={label(labelKey)}
        value={value}
        required
        error={fieldError(field)}
      >
        <scds-picker-option value="yes">{label('employment-insurance.application.yesOption')}</scds-picker-option>
        <scds-picker-option value="no">{label('employment-insurance.application.noOption')}</scds-picker-option>
      </scds-picker>
    );
  }

  // Step 1: "Confirm what we already know" -- every personal and employment
  // field, once (spec 014). EiPreFillConfirmStep lays it out; the draft,
  // validation and the address write-back live here.
  function renderStep1() {
    return (
      <EiPreFillConfirmStep
        draft={draft}
        setDraft={setDraft}
        label={label}
        fieldError={fieldError}
        locale={locale}
        contentLocale={loadedLocale}
        profileStatus={profileStatus}
        employmentStatus={employmentStatus}
        hasSeparation={separation !== null}
        totals={insurableTotals}
        patching={patching}
        patchError={patchError}
      />
    );
  }

  // Step 2: "A few things only you can answer" -- the required availability
  // question beside spec 013's optional checklist (composed, unchanged).
  function renderStep2() {
    return (
      <div className="ei-form__step">
        <p className="ei-form__hint">{label('employment-insurance.application.otherSituationsIntro')}</p>
        <section className="ei-section">
          {renderYesNoPicker(
            'availableImmediately',
            'employment-insurance.application.availableImmediatelyLabel',
            draft.availableImmediately,
          )}
          {draft.availableImmediately === 'no' ? (
            <scds-text-input
              data-field="availableFromDate"
              label={label('employment-insurance.application.availableFromDateLabel')}
              type="date"
              value={draft.availableFromDate}
              required
              error={fieldError('availableFromDate')}
            />
          ) : null}
        </section>
        {/* Spec 013: every box is optional and stored as a plain boolean;
            none branches the flow or asks follow-up questions. Native
            checkboxes, not an SCDS element -- SCDS has no checkbox -- with
            plain React onChange. The card styling sits on the wrapper, not
            the fieldset, so the legend stays an ordinary in-flow legend (no
            float) -- the fieldset's accessible group name. */}
        <div className="ei-checklist">
          <fieldset className="ei-fieldset" aria-describedby={checklistHintId}>
          <legend className="ei-checklist__heading">
            {label('employment-insurance.application.otherSituationsHeading')}
          </legend>
          <p id={checklistHintId} className="ei-checklist__hint">
            {label('employment-insurance.application.otherSituationsHint')}
          </p>
          <div className="ei-checklist__options">
            {OTHER_SITUATION_OPTIONS.map(({ field, labelKey }) => (
              <label key={field} className="ei-checklist__option">
                <input
                  type="checkbox"
                  className="ei-checklist__checkbox"
                  checked={draft[field]}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setDraft((prev) => ({ ...prev, [field]: checked }));
                  }}
                />
                <span>{label(labelKey)}</span>
              </label>
            ))}
          </div>
          </fieldset>
        </div>
      </div>
    );
  }

  // Step 3: "How you'll be paid". The payment method is the existing
  // directDepositEnrolling answer ('yes' = direct deposit, 'no' = cheque),
  // so the submitted directDeposit.enrolling is unchanged; T4E delivery and
  // tax deduction are spec 014's stored-only preferences, both required
  // with no default.
  function renderStep3() {
    return (
      <div className="ei-form__step">
        <section className="ei-section">
          <scds-picker
            key={`directDepositEnrolling-${loadedLocale ?? 'loading'}`}
            data-field="directDepositEnrolling"
            label={label('employment-insurance.application.paymentMethodLabel')}
            value={draft.directDepositEnrolling}
            required
            error={fieldError('directDepositEnrolling')}
          >
            <scds-picker-option value="yes">
              {label('employment-insurance.application.paymentDirectDepositOption')}
            </scds-picker-option>
            <scds-picker-option value="no">{label('employment-insurance.application.paymentChequeOption')}</scds-picker-option>
          </scds-picker>
          {draft.directDepositEnrolling === 'yes' ? (
            <>
              <scds-text-input
                data-field="institutionNumber"
                label={label('employment-insurance.application.institutionNumberLabel')}
                value={draft.institutionNumber}
                required
                maxlength={3}
                error={fieldError('institutionNumber')}
              />
              <scds-text-input
                data-field="transitNumber"
                label={label('employment-insurance.application.transitNumberLabel')}
                value={draft.transitNumber}
                required
                maxlength={5}
                error={fieldError('transitNumber')}
              />
              <scds-text-input
                data-field="accountNumber"
                label={label('employment-insurance.application.accountNumberLabel')}
                value={draft.accountNumber}
                required
                error={fieldError('accountNumber')}
              />
            </>
          ) : draft.directDepositEnrolling === 'no' ? (
            <p className="ei-form__hint">{label('employment-insurance.application.paymentChequeNote')}</p>
          ) : null}
        </section>
        <section className="ei-section">
          <scds-picker
            key={`taxSlipDelivery-${loadedLocale ?? 'loading'}`}
            data-field="taxSlipDelivery"
            label={label('employment-insurance.application.taxSlipLabel')}
            hint={label('employment-insurance.application.taxSlipHint')}
            value={draft.taxSlipDelivery}
            required
            error={fieldError('taxSlipDelivery')}
          >
            <scds-picker-option value="online">{label('employment-insurance.application.taxSlipOnlineOption')}</scds-picker-option>
            <scds-picker-option value="mail">{label('employment-insurance.application.taxSlipMailOption')}</scds-picker-option>
          </scds-picker>
        </section>
        <section className="ei-section">
          <scds-picker
            key={`taxDeduction-${loadedLocale ?? 'loading'}`}
            data-field="taxDeduction"
            label={label('employment-insurance.application.taxDeductionLabel')}
            hint={label('employment-insurance.application.taxDeductionHint')}
            value={draft.taxDeduction}
            required
            error={fieldError('taxDeduction')}
          >
            <scds-picker-option value="basic">
              {label('employment-insurance.application.taxDeductionBasicOption')}
            </scds-picker-option>
            <scds-picker-option value="basic_and_spouse">
              {label('employment-insurance.application.taxDeductionBasicAndSpouseOption')}
            </scds-picker-option>
          </scds-picker>
        </section>
      </div>
    );
  }

  // Step 4: "Review and submit". No personal/employment field is repeated
  // here (spec 014: each appears on exactly one step) -- the review card
  // links back to step 1 instead, as the prototype's does. The estimate is
  // weekly-only, from the same calculateWeeklyBenefitAmount the BFF uses.
  function renderStep4() {
    const weeklyEstimate =
      draft.payRate !== null && !isBlank(draft.payPeriod)
        ? new Intl.NumberFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', { style: 'currency', currency: 'CAD' }).format(
            calculateWeeklyBenefitAmount({
              payRate: draft.payRate,
              payPeriod: draft.payPeriod as EiApplicationInput['separation']['payPeriod'],
            }),
          )
        : '—';
    const consentMissing =
      errors.has('statementsConfirmed') || errors.has('consentToShare') || errors.has('rightsAccepted');
    return (
      <div className="ei-form__step">
        <section className="ei-section">
          <h3 className="ei-section__title">{label('employment-insurance.application.reviewHeading')}</h3>
          <p className="ei-form__hint">{label('employment-insurance.application.reviewBody')}</p>
          <button
            type="button"
            className="ei-section__link"
            disabled={busy}
            onClick={() => {
              setErrors(new Set());
              setStep(1);
            }}
          >
            {label('employment-insurance.application.reviewFullApplicationLink')}
          </button>
        </section>

        <p role="note" className="ei-estimate-note">
          {label('employment-insurance.application.estimateNote')}
        </p>
        <section className="ei-estimate">
          <div className="ei-estimate__band">{label('employment-insurance.application.estimateBand')}</div>
          <div className="ei-estimate__body">
            <h3 className="ei-section__title">{label('employment-insurance.application.estimateTitle')}</h3>
            <p className="ei-estimate__amount">{weeklyEstimate}</p>
            <p className="ei-estimate__caption">{label('employment-insurance.application.estimatePerWeekLabel')}</p>
          </div>
        </section>

        {/* Card on the wrapper, plain fieldset + in-flow legend inside, as
            for step 2's checklist, so "Declaration" names the consent group. */}
        <section className="ei-section ei-declaration">
          <fieldset className="ei-fieldset" aria-describedby={consentMissing ? declarationErrorId : undefined}>
          <legend className="ei-section__title">{label('employment-insurance.application.declarationHeading')}</legend>
          <p className="ei-declaration__intro">{label('employment-insurance.application.declarationIntro')}</p>
          <ul className="ei-declaration__statements">
            {DECLARATION_STATEMENT_KEYS.map((key) => (
              <li key={key}>{label(key)}</li>
            ))}
          </ul>
          <div className="ei-checklist__options">
            {CONSENT_OPTIONS.map(({ field, labelKey }) => (
              <label key={field} className="ei-checklist__option">
                <input
                  type="checkbox"
                  className="ei-checklist__checkbox"
                  required
                  aria-invalid={errors.has(field) || undefined}
                  checked={draft[field]}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setDraft((prev) => ({ ...prev, [field]: checked }));
                  }}
                />
                <span>{label(labelKey)}</span>
              </label>
            ))}
          </div>
          {consentMissing ? (
            <p id={declarationErrorId} role="alert" className="ei-form__error">
              {label('employment-insurance.application.consentsRequiredError')}
            </p>
          ) : null}
          </fieldset>
        </section>

        <section className="ei-section">
          <h3 className="ei-section__title">{label('employment-insurance.application.thirdPartyHeading')}</h3>
          {renderYesNoPicker(
            'thirdPartyAssistance',
            'employment-insurance.application.thirdPartyLabel',
            draft.thirdPartyAssistance,
          )}
          {draft.thirdPartyAssistance === 'yes' ? (
            <scds-text-input
              data-field="helperName"
              label={label('employment-insurance.application.helperNameLabel')}
              value={draft.helperName}
            />
          ) : null}
        </section>
      </div>
    );
  }

  const steps: Record<number, () => React.ReactNode> = {
    1: renderStep1,
    2: renderStep2,
    3: renderStep3,
    4: renderStep4,
  };

  return (
    <div ref={rootRef} className="ei-form">
      <scds-breadcrumbs>
        <scds-breadcrumbs-item href="/">{label('employment-insurance.application.breadcrumbHome')}</scds-breadcrumbs-item>
        <scds-breadcrumbs-item href="/employment-insurance">
          {label('employment-insurance.application.breadcrumbEi')}
        </scds-breadcrumbs-item>
        <scds-breadcrumbs-item>{label('employment-insurance.application.breadcrumbCurrent')}</scds-breadcrumbs-item>
      </scds-breadcrumbs>
      <scds-progress-bar current={step} total={TOTAL_STEPS} step-label={stepTitles[step - 1]} />
      <scds-heading tag="h2">{stepTitles[step - 1]}</scds-heading>
      {steps[step]()}
      <div className="ei-form__actions">
        {step > 1 ? (
          <scds-button
            variant="secondary"
            type="button"
            disabled={busy}
            onClick={() => {
              if (!busy) goBack();
            }}
          >
            {label('employment-insurance.application.backButton')}
          </scds-button>
        ) : null}
        <scds-button
          variant="primary"
          type="button"
          disabled={busy}
          onClick={() => {
            if (!busy) void goNext();
          }}
        >
          {step === TOTAL_STEPS ? (
            label('employment-insurance.application.submitButton')
          ) : step === 1 ? (
            // Spec 014 keeps a single "Confirm and continue →" for step 1
            // (no per-section confirmations).
            <>
              {label('employment-insurance.application.confirmAndContinueButton')}
              <scds-icon name="arrow-right" size="sm" aria-hidden="true" />
            </>
          ) : step === 2 ? (
            // Spec 013 §3 labels this step's primary button
            // "Continue to payment →", arrow included -- and since spec 014
            // the next step really is payment.
            <>
              {label('employment-insurance.application.continueToPaymentButton')}
              <scds-icon name="arrow-right" size="sm" aria-hidden="true" />
            </>
          ) : (
            label('employment-insurance.application.nextButton')
          )}
        </scds-button>
      </div>
    </div>
  );
}
